export enum CiProviderId {
  bitrise = 'bitrise',
  circleCi = 'circle-ci',
  gitlabCi = 'gitlab-ci',
  jenkins = 'jenkins',
  githubActions = 'github-actions',
  bitbucketPipelines = 'bitbucket-pipelines',
  buildkite = 'buildkite',
  cloudBuild = 'cloud-build',
  bamboo = 'bamboo',
  local = 'n/a',
  other = 'other',
  unknown = 'unknown',
}

export type CiProvider = {
  id: CiProviderId;
  name: string;
};

export const ciProviderBitrise: CiProvider = {
  id: CiProviderId.bitrise,
  name: 'Bitrise CI',
};

export const ciProviderCircleCI: CiProvider = {
  id: CiProviderId.circleCi,
  name: 'CircleCI',
};

const ciProviderGitlabCI: CiProvider = {
  id: CiProviderId.gitlabCi,
  name: 'GitLab CI',
};

const ciProviderJenkins: CiProvider = {
  id: CiProviderId.jenkins,
  name: 'Jenkins',
};

const ciProviderBamboo: CiProvider = {
  id: CiProviderId.bamboo,
  name: 'Bamboo',
};

const ciProviderCloudBuild: CiProvider = {
  id: CiProviderId.cloudBuild,
  name: 'Cloud build',
};

export const ciProviderGithubActions: CiProvider = {
  id: CiProviderId.githubActions,
  name: 'GitHub Actions',
};

const ciProviderBitbucketPipelines: CiProvider = {
  id: CiProviderId.bitbucketPipelines,
  name: 'Bitbucket Pipelines',
};

const ciProviderBuildkite: CiProvider = {
  id: CiProviderId.buildkite,
  name: 'Buildkite',
};

const ciProviderOther: CiProvider = {
  id: CiProviderId.other,
  name: 'Other',
};

const ciProviderLocal: CiProvider = {
  id: CiProviderId.local,
  name: 'Local',
};

const ciProviderUnknown: CiProvider = {
  id: CiProviderId.unknown,
  name: 'Unknown',
};

export const featuredBuildCacheEnvironments = {
  [CiProviderId.bitrise]: ciProviderBitrise,
  [CiProviderId.circleCi]: ciProviderCircleCI,
  [CiProviderId.githubActions]: ciProviderGithubActions,
  [CiProviderId.gitlabCi]: ciProviderGitlabCI,
  [CiProviderId.jenkins]: ciProviderJenkins,
  [CiProviderId.buildkite]: ciProviderBuildkite,
  [CiProviderId.bitbucketPipelines]: ciProviderBitbucketPipelines,
  [CiProviderId.cloudBuild]: ciProviderCloudBuild,
  [CiProviderId.bamboo]: ciProviderBamboo,
  [CiProviderId.other]: ciProviderOther,
};

const CiProviders = {
  ...featuredBuildCacheEnvironments,
  [CiProviderId.local]: ciProviderLocal,
  [CiProviderId.unknown]: ciProviderUnknown,
};

// The metric API emits raw CiProviderId values; fall back to the raw id for providers we don't know.
export const getCiProviderName = (id: string): string => CiProviders[id as CiProviderId]?.name ?? id;

export const getCiProviderIdByName = (name: string): CiProviderId | undefined =>
  Object.values(CiProviders).find((provider) => provider.name === name)?.id;

// A Build Cache invocation that didn't run on CI carries no provider id, which the backend resolves to
// `unknown` — that is what the product calls a local build environment (ACI-5287). The Build Cache CLI shares
// the convention: `bitrise-build-cache browse` deep-links here with `ci_provider=unknown` to show local runs.
// `local` ('n/a') is the explicit id for the same thing — no Build Cache client emits it today, but Insights
// does, and it is the natural value if the clients are ever fixed to report locality directly.
export const isLocalBuildEnvironment = (ciProvider?: CiProviderId): boolean =>
  ciProvider === CiProviderId.unknown || ciProvider === CiProviderId.local;

// Deliberately separate from getCiProviderName: Insights' attribute endpoints emit `n/a` and `unknown` as
// distinct values, where `unknown` genuinely means unknown, so this rename must not leak outside Build Cache.
export const getBuildCacheCiProviderName = (ciProvider: CiProviderId): string =>
  isLocalBuildEnvironment(ciProvider) ? ciProviderLocal.name : getCiProviderName(ciProvider);

export default CiProviders;
